import { useState, useEffect } from "react";
import DashboardLayout from "../components/layout/DashboardLayout";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { useAuth } from "../context/AuthContext";
import api, { formatCurrency, formatPercent } from "../lib/api";
import { toast } from "sonner";
import {
  BarChart3, TrendingUp, TrendingDown, DollarSign, Users, Activity, Wallet,
} from "lucide-react";
import {
  AreaChart, Area, BarChart, Bar, CartesianGrid, Legend, ResponsiveContainer,
  Tooltip, XAxis, YAxis,
} from "recharts";

const AnalyticsPage = () => {
  const { user, isAdmin } = useAuth();
  const [summary, setSummary] = useState(null);
  const [timeseries, setTimeseries] = useState([]);
  const [users, setUsers] = useState([]);
  const [personal, setPersonal] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        if (isAdmin) {
          const [sumRes, tsRes, uRes] = await Promise.all([
            api.get("/analytics/summary"),
            api.get("/analytics/performance?days=365"),
            api.get("/analytics/users"),
          ]);
          setSummary(sumRes.data);
          setTimeseries(tsRes.data.series || []);
          setUsers(uRes.data.users || []);
        }
        const meRes = await api.get("/analytics/me");
        setPersonal(meRes.data);
      } catch (e) {
        toast.error("Failed to load analytics");
      } finally {
        setLoading(false);
      }
    })();
  }, [isAdmin]);

  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="analytics-page">
        <div>
          <h1 className="text-3xl font-serif text-foreground flex items-center gap-3">
            <BarChart3 className="text-gold" /> Investment Fund Analytics
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            Aggregate performance across native deposits and partner demo accounts.
          </p>
        </div>

        {loading ? (
          <div className="text-center py-12 text-muted-foreground">Loading analytics…</div>
        ) : (
          <Tabs defaultValue={isAdmin ? "platform" : "personal"} className="w-full">
            <TabsList>
              {isAdmin && <TabsTrigger value="platform" data-testid="tab-platform">Platform</TabsTrigger>}
              <TabsTrigger value="personal" data-testid="tab-personal">Personal</TabsTrigger>
              {isAdmin && <TabsTrigger value="users" data-testid="tab-users">Per User</TabsTrigger>}
            </TabsList>

            {isAdmin && summary && (
              <TabsContent value="platform" className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                  <Metric title="Total AUM" icon={Wallet} value={formatCurrency(summary.combined.total_aum)} sub={`${summary.qualified_users} qualified users`} />
                  <Metric title="Total Deposits" icon={DollarSign} value={formatCurrency(summary.combined.total_deposits)} sub="All sources" />
                  <Metric title="Total P/L" icon={TrendingUp} value={formatCurrency(summary.combined.total_pnl)} sub={formatPercent(summary.combined.return_pct)} accent={summary.combined.total_pnl >= 0 ? "text-emerald" : "text-red-400"} />
                  <Metric title="Demo Accounts" icon={Activity} value={summary.monaxa.accounts_count || 0} sub={`Partner ${summary.monaxa.accounts_count}`} />
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  <Card className="bg-surface border-platinum/10">
                    <CardHeader><CardTitle>Native Fund</CardTitle><CardDescription>On-platform deposits, withdrawals, gains & losses</CardDescription></CardHeader>
                    <CardContent className="grid grid-cols-2 gap-3 text-sm">
                      <Stat label="Deposits" value={formatCurrency(summary.native.total_deposits)} />
                      <Stat label="Withdrawals" value={formatCurrency(summary.native.total_withdrawals)} />
                      <Stat label="Gains" value={formatCurrency(summary.native.total_gains)} accent="text-emerald" />
                      <Stat label="Losses" value={formatCurrency(summary.native.total_losses)} accent="text-red-400" />
                      <Stat label="Balance" value={formatCurrency(summary.native.current_balance)} />
                      <Stat label="Net Perf" value={formatPercent(summary.native.performance_pct)} accent={summary.native.performance_pct >= 0 ? "text-emerald" : "text-red-400"} />
                    </CardContent>
                  </Card>
                  <Card className="bg-surface border-platinum/10">
                    <CardHeader><CardTitle>Partner Demo</CardTitle><CardDescription>Partner account aggregates (MT4/MT5/cTrader)</CardDescription></CardHeader>
                    <CardContent className="grid grid-cols-2 gap-3 text-sm">
                      <Stat label="Balance" value={formatCurrency(summary.monaxa.total_balance)} />
                      <Stat label="Equity" value={formatCurrency(summary.monaxa.total_equity)} />
                      <Stat label="Avg Return" value={formatPercent(summary.monaxa.avg_return_pct)} accent={summary.monaxa.avg_return_pct >= 0 ? "text-emerald" : "text-red-400"} />
                      <Stat label="Deposits" value={formatCurrency(summary.monaxa.total_deposits)} />
                      <Stat label="Withdrawals" value={formatCurrency(summary.monaxa.total_withdrawals)} />
                    </CardContent>
                  </Card>
                </div>

                {timeseries.length > 0 && (
                  <Card className="bg-surface border-platinum/10">
                    <CardHeader><CardTitle>Monthly Fund Movement</CardTitle><CardDescription>Running AUM over time</CardDescription></CardHeader>
                    <CardContent className="h-80">
                      <ResponsiveContainer width="100%" height="100%">
                        <AreaChart data={timeseries}>
                          <defs>
                            <linearGradient id="aumGrad" x1="0" y1="0" x2="0" y2="1">
                              <stop offset="0%" stopColor="#c7a86a" stopOpacity={0.5} />
                              <stop offset="100%" stopColor="#c7a86a" stopOpacity={0} />
                            </linearGradient>
                          </defs>
                          <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.05)" />
                          <XAxis dataKey="month" stroke="#8a8a8a" fontSize={12} />
                          <YAxis stroke="#8a8a8a" fontSize={12} />
                          <Tooltip contentStyle={{ background: "#1a1a1a", border: "1px solid rgba(255,255,255,0.1)" }} formatter={(v) => formatCurrency(v)} />
                          <Area type="monotone" dataKey="running_aum" stroke="#c7a86a" fill="url(#aumGrad)" strokeWidth={2} />
                        </AreaChart>
                      </ResponsiveContainer>
                    </CardContent>
                  </Card>
                )}

                {timeseries.length > 0 && (
                  <Card className="bg-surface border-platinum/10">
                    <CardHeader><CardTitle>Monthly Flows</CardTitle><CardDescription>Deposits vs withdrawals vs gains/losses</CardDescription></CardHeader>
                    <CardContent className="h-80">
                      <ResponsiveContainer width="100%" height="100%">
                        <BarChart data={timeseries}>
                          <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.05)" />
                          <XAxis dataKey="month" stroke="#8a8a8a" fontSize={12} />
                          <YAxis stroke="#8a8a8a" fontSize={12} />
                          <Tooltip contentStyle={{ background: "#1a1a1a", border: "1px solid rgba(255,255,255,0.1)" }} formatter={(v) => formatCurrency(v)} />
                          <Legend />
                          <Bar dataKey="deposits" fill="#10b981" name="Deposits" />
                          <Bar dataKey="withdrawals" fill="#f59e0b" name="Withdrawals" />
                          <Bar dataKey="gains" fill="#c7a86a" name="Gains" />
                          <Bar dataKey="losses" fill="#ef4444" name="Losses" />
                        </BarChart>
                      </ResponsiveContainer>
                    </CardContent>
                  </Card>
                )}
              </TabsContent>
            )}

            {personal && (
              <TabsContent value="personal" className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {/* When the member IS mapped to a partner account but its
                      balance could not be read, the partner leg is UNKNOWN —
                      not zero. Adding 0 would understate their position and
                      present it as a total. Show the native figure alone and
                      say why (STARSEED-CAPMARKETS-094). */}
                  <Metric
                    title="My Balance (Combined)"
                    icon={Wallet}
                    value={formatCurrency(
                      personal.native.current_balance
                      + (personal.monaxa_balance_known === false ? 0 : (personal.monaxa?.balance || 0))
                    )}
                    sub={personal.monaxa_balance_known === false
                      ? "Native only — partner balance unavailable"
                      : "Native + Partner"}
                  />
                  <Metric
                    title="Native P/L"
                    icon={TrendingUp}
                    value={formatCurrency(personal.native.net_pnl)}
                    sub={formatPercent(personal.native.return_pct)}
                    accent={personal.native.net_pnl >= 0 ? "text-emerald" : "text-red-400"}
                  />
                  {/* CAPMARKETS-231. A "Partner P/L" metric captioned with a
                      count of OPEN POSITIONS was here, and an aggregate
                      "Open P/L" stat was in the Partner card above. Both are
                      removed for the same reason as the ones on
                      PartnerAccountPage: the partner asked that open trades not
                      be shown, and EchoSense presents a managed holding rather
                      than a trading terminal.

                      This page was missed by that change because it is reached
                      only by URL — it carries no nav entry — and the sweep that
                      found the others searched `*.jsx` while the import that
                      routes this one lives in `App.js`. It is behind
                      `ProtectedRoute` with no role or module gate, so any
                      signed-in member could open /analytics and see it. */}
                </div>

                {personal.timeline.length > 0 && (
                  <Card className="bg-surface border-platinum/10">
                    <CardHeader><CardTitle>My Monthly Balance</CardTitle></CardHeader>
                    <CardContent className="h-72">
                      <ResponsiveContainer width="100%" height="100%">
                        <AreaChart data={personal.timeline}>
                          <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.05)" />
                          <XAxis dataKey="month" stroke="#8a8a8a" />
                          <YAxis stroke="#8a8a8a" />
                          <Tooltip contentStyle={{ background: "#1a1a1a", border: "1px solid rgba(255,255,255,0.1)" }} formatter={(v) => formatCurrency(v)} />
                          <Area type="monotone" dataKey="running_balance" stroke="#c7a86a" fill="#c7a86a33" strokeWidth={2} />
                        </AreaChart>
                      </ResponsiveContainer>
                    </CardContent>
                  </Card>
                )}
              </TabsContent>
            )}

            {isAdmin && (
              <TabsContent value="users">
                <Card className="bg-surface border-platinum/10">
                  <CardHeader>
                    <CardTitle>Per-User Fund Breakdown</CardTitle>
                    <CardDescription>Qualified clients sorted by combined balance</CardDescription>
                  </CardHeader>
                  <CardContent className="p-0">
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm">
                        <thead className="border-b border-platinum/10 text-muted-foreground">
                          <tr>
                            <th className="text-left p-3">User</th>
                            <th className="text-left p-3">Role</th>
                            <th className="text-right p-3">Native Bal</th>
                            <th className="text-right p-3">Native P/L</th>
                            <th className="text-right p-3">Partner Bal</th>
                            <th className="text-right p-3">Partner P/L</th>
                            <th className="text-right p-3">Pocket Bal</th>
                            <th className="text-right p-3">Pocket P/L</th>
                            <th className="text-right p-3">Combined</th>
                          </tr>
                        </thead>
                        <tbody>
                          {users.length === 0 ? (
                            <tr><td colSpan="9" className="p-6 text-center text-muted-foreground">No qualified users yet</td></tr>
                          ) : users.map((u) => (
                            <tr key={u.user_id} className="border-b border-platinum/5 hover:bg-platinum/5" data-testid={`analytics-row-${u.user_id}`}>
                              <td className="p-3">
                                <div className="font-medium">{u.full_name}</div>
                                <div className="text-xs text-muted-foreground">{u.email}</div>
                              </td>
                              <td className="p-3"><Badge variant="outline" className="capitalize">{u.role}</Badge></td>
                              <td className="p-3 text-right">{formatCurrency(u.native_balance)}</td>
                              <td className={`p-3 text-right ${u.native_pnl >= 0 ? "text-emerald" : "text-red-400"}`}>{formatCurrency(u.native_pnl)}</td>
                              {/* "—" means the member is mapped to a partner
                                  account whose balance could not be read, i.e.
                                  UNKNOWN. Printing $0 here hid exactly the
                                  case this table exists to surface: funds in,
                                  mapping stale or never fetched
                                  (STARSEED-CAPMARKETS-094). */}
                              <td
                                className="p-3 text-right"
                                title={u.monaxa_balance_known === false
                                  ? "Mapped to a partner account, but its balance could not be read"
                                  : undefined}
                              >
                                {u.monaxa_balance_known === false ? "—" : formatCurrency(u.monaxa_balance)}
                              </td>
                              <td className={`p-3 text-right ${u.monaxa_balance_known === false ? "" : (u.monaxa_pnl >= 0 ? "text-emerald" : "text-red-400")}`}>
                                {u.monaxa_balance_known === false ? "—" : formatCurrency(u.monaxa_pnl)}
                              </td>
                              <td className="p-3 text-right">{formatCurrency(u.pocket_balance || 0)}</td>
                              <td className={`p-3 text-right ${(u.pocket_pnl || 0) >= 0 ? "text-emerald" : "text-red-400"}`}>{formatCurrency(u.pocket_pnl || 0)}</td>
                              <td
                                className="p-3 text-right font-semibold"
                                title={u.combined_balance_known === false
                                  ? "Floor, not a total — the partner leg could not be read"
                                  : undefined}
                              >
                                {formatCurrency(u.combined_balance)}
                                {u.combined_balance_known === false && (
                                  <span className="text-muted-foreground font-normal ml-1">+?</span>
                                )}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </CardContent>
                </Card>
              </TabsContent>
            )}
          </Tabs>
        )}
      </div>
    </DashboardLayout>
  );
};

const Metric = ({ title, value, sub, icon: Icon, accent = "text-foreground" }) => (
  <Card className="bg-surface border-platinum/10">
    <CardContent className="p-5">
      <div className="flex items-center justify-between">
        <p className="text-xs uppercase tracking-wide text-muted-foreground">{title}</p>
        <Icon className="text-gold" size={18} />
      </div>
      <p className={`text-2xl font-semibold mt-2 ${accent}`}>{value}</p>
      {sub && <p className="text-xs text-muted-foreground mt-1">{sub}</p>}
    </CardContent>
  </Card>
);

const Stat = ({ label, value, accent = "text-foreground" }) => (
  <div>
    <p className="text-xs text-muted-foreground uppercase tracking-wide">{label}</p>
    <p className={`font-semibold mt-0.5 ${accent}`}>{value}</p>
  </div>
);

export default AnalyticsPage;
